import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  BdGrantRequestsService,
  BdPermissionsUsersService,
  GrantRequest,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import { ShareableType } from '@bindoc-projects/resource-domain-lib';
import { ButtonDirective } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { TableModule } from 'primeng/table';
import { take } from 'rxjs';

import {
  AcceptGrantRequestDialogData,
  AcceptGrantRequestDialogResult,
  BdAcceptGrantRequestDialogComponent,
} from '../inbox-tab/accept-grant-request-dialog.component';

interface ResolvedPendingRequest extends GrantRequest {
  requesterName: string;
}

@Component({
  selector: 'bd-pending-requests-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (requests().length > 0) {
      <p-table [value]="requests()" stripedRows>
        <ng-template #header>
          <tr>
            <th>Anfrager</th>
            <th>Datum</th>
            <th>Kommentar</th>
            <th></th>
          </tr>
        </ng-template>
        <ng-template #body let-request>
          <tr>
            <td>{{ request.requesterName }}</td>
            <td>{{ request.createdDate }}</td>
            <td>{{ request.comment ?? '' }}</td>
            <td>
              <div class="flex gap-2">
                <!--
                  Both are row actions (02), so neither may be the filled
                  primary: with no severity "Annehmen" rendered orange once per
                  row, and the view allows at most one primary. The pair stays
                  distinguishable by treatment instead — filled for the
                  affirmative, text for the dismissive.
                -->
                <button
                  pButton
                  size="small"
                  severity="secondary"
                  (click)="onAccept(request)"
                >
                  <span>Annehmen</span>
                </button>
                <button
                  pButton
                  text
                  size="small"
                  severity="secondary"
                  (click)="onDismiss(request)"
                >
                  <span>Ablehnen</span>
                </button>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    }
  `,
  imports: [TableModule, ButtonDirective],
})
export class BdPendingRequestsTableComponent {
  public readonly shareableType = input.required<ShareableType>();
  public readonly shareableId = input.required<string>();
  public readonly shareableName = input<string>('');
  public readonly requestResolved = output<void>();

  public readonly requests = signal<ResolvedPendingRequest[]>([]);

  private readonly _grantRequestsService = inject(BdGrantRequestsService);
  private readonly _usersService = inject(BdPermissionsUsersService);
  private readonly _dialogService = inject(DialogService);

  constructor() {
    effect(() => {
      const type = this.shareableType();
      const id = this.shareableId();
      this._loadRequests(type, id);
    });
  }

  public onAccept(request: ResolvedPendingRequest): void {
    const data: AcceptGrantRequestDialogData = {
      grantRequest: request,
      requesterName: request.requesterName,
      shareableName: this.shareableName(),
    };
    const ref = this._dialogService.open(BdAcceptGrantRequestDialogComponent, {
      header: 'Zugriff gewähren',
      data,
    });
    ref.onClose
      .pipe(take(1))
      .subscribe((result?: AcceptGrantRequestDialogResult | null) => {
        if (!result) {
          return;
        }
        this._grantRequestsService
          .accept(request.id, { permission: result.permission })
          .pipe(take(1))
          .subscribe(() => {
            this.requestResolved.emit();
            this._loadRequests(this.shareableType(), this.shareableId());
          });
      });
  }

  public onDismiss(request: ResolvedPendingRequest): void {
    this._grantRequestsService
      .dismiss(request.id)
      .pipe(take(1))
      .subscribe(() => {
        this.requestResolved.emit();
        this._loadRequests(this.shareableType(), this.shareableId());
      });
  }

  private _loadRequests(type: ShareableType, id: string): void {
    this._grantRequestsService
      .forShareable(type, id, 'WAITING')
      .pipe(take(1))
      .subscribe((requests) => {
        const resolved: ResolvedPendingRequest[] = requests.map((r) => ({
          ...r,
          requesterName: r.requesterId,
        }));
        this.requests.set(resolved);
        for (const r of requests) {
          this._usersService
            .getById(r.requesterId)
            .pipe(take(1))
            .subscribe((user) => {
              if (!user) {
                return;
              }
              this._patchRequester(r.id, user.displayName);
            });
        }
      });
  }

  private _patchRequester(requestId: string, requesterName: string): void {
    this.requests.update((current) =>
      current.map((r) => (r.id === requestId ? { ...r, requesterName } : r)),
    );
  }
}
